import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import BackButton from '@/ui/BackButton/BackButton';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';
import { IconCode, IconHardware, IconSaveWhite } from '@/icons';
import { StyledHeader } from '../styles';
import { SaveChangesModal } from '../../SaveChangesModal/SaveChangesModal';
import CodeEditingSwitch from './CodeEditingSwitch';

type HeaderProps = {
  handleSave: () => Promise<any> | Error;
  enableSave: boolean;
  isSaving: boolean;
  setIsHardwareModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isViewingCode: boolean;
  toggleCodeView: () => void;
  isEditingCode: boolean;
  setIsEditingCode: React.Dispatch<React.SetStateAction<boolean>>;
  saveScript: () => Promise<void>;
  savingScript: boolean;
  refetchScript: () => void;
  refetchUnit: () => void;
};

function Header({
  handleSave,
  enableSave,
  isSaving,
  setIsHardwareModalOpen,
  isViewingCode,
  toggleCodeView,
  isEditingCode,
  setIsEditingCode,
  saveScript,
  savingScript,
  refetchScript,
  refetchUnit,
}: HeaderProps) {
  const { id: unitId, title } = useParams();
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const navigate = useNavigate();
  const intl = useIntl();

  const save = async () => {
    try {
      if (isEditingCode) {
        // if editing code, save the code directly, no need to refetch the script, since the code is the source of truth
        // also update the unit settings to disable the logic editor
        await saveScript();
        await refetchScript();
        await refetchUnit();
      } else {
        // if not editing code, just save the flow and refetch the script to directly show the updated code
        // here refetchScript is already called in handleSave in LogicEditor.tsx
        await handleSave();
      }
      // refetch the script and unit to display the latest data
    } catch (error) {
      console.error('Error while saving', error);
    }
  };
  return (
    <StyledHeader data-testid="header">
      <SaveChangesModal
        open={isSaveModalOpen}
        isSaving={isSaving || savingScript}
        onReturnToEditing={() => {
          setIsSaveModalOpen(false);
        }}
        onDoNotSave={() => {
          setIsSaveModalOpen(false);
          navigate(`/upload/${unitId}`);
        }}
        onSave={() => {
          save();
          setIsSaveModalOpen(false);
          navigate(`/upload/${unitId}`);
        }}
      />

      <Flex align="center" gap={8}>
        <BackButton
          onClick={() => {
            if (enableSave) {
              setIsSaveModalOpen(true);
            } else {
              navigate(`/upload/${unitId}`);
            }
          }}
        />
        <T h1>{title ?? 'Logic-Editor'}</T>
      </Flex>
      <Flex gap={16} align="center">
        {isViewingCode && (
          <CodeEditingSwitch
            isSaving={isSaving}
            savingScript={savingScript}
            isEditingCode={isEditingCode}
            setIsEditingCode={setIsEditingCode}
          />
        )}
        {!isEditingCode && (
          <>
            <Button
              size="large"
              mode="ghost"
              onClick={() => setIsHardwareModalOpen(true)}
              testId="open-hardware-modal-button"
            >
              <IconHardware />
              <T p color="#8D8D8D">
                {intl.formatMessage({
                  defaultMessage: 'Hardware',
                  id: '9gyxgq',
                })}
              </T>
            </Button>

            <Button
              size="large"
              mode="ghost"
              onClick={() => toggleCodeView()}
              testId="show-code-button"
            >
              <IconCode />
              <T p color="#8D8D8D">
                {isViewingCode
                  ? intl.formatMessage({
                      defaultMessage: 'Show Logic',
                      id: 'zeke+Z',
                    })
                  : intl.formatMessage({
                      defaultMessage: 'Show Code',
                      id: 'fp7Q+F',
                    })}
              </T>
            </Button>
          </>
        )}
        <Button
          size="large"
          onClick={() => save()}
          disabled={!enableSave || isSaving || savingScript}
          loading={isSaving || savingScript}
          testId="save-button"
        >
          <IconSaveWhite />
          {intl.formatMessage({ defaultMessage: 'Save', id: 'jvo0vs' })}
        </Button>
      </Flex>
    </StyledHeader>
  );
}

export default Header;
